import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, type Project, type RequestSummary, type Workspace } from "../lib/api";
import { writeActiveProjectId } from "./Layout";
import { formatCombo } from "../lib/keybindings";
import { effectiveTheme, readMode, setMode } from "../lib/theme";

interface Props {
  open: boolean;
  onClose: () => void;
  workspace: Workspace;
  onManageEnvs: () => void;
  onManageAgents: () => void;
}

interface Cmd {
  id: string;
  label: string;
  hint?: string;
  group: string;
  run: () => void;
  shortcut?: string;
}

// Fuzzy substring matcher — returns a score or -1. Higher is better.
function score(haystack: string, needle: string): number {
  if (!needle) return 0;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h.includes(n)) return 100 - (h.indexOf(n));   // earlier matches rank higher
  // Subsequence match
  let hi = 0, hits = 0;
  for (const ch of n) {
    const i = h.indexOf(ch, hi);
    if (i < 0) return -1;
    hits++;
    hi = i + 1;
  }
  return hits;
}

export default function CommandPalette({ open, onClose, workspace, onManageEnvs, onManageAgents }: Props) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const projects = useQuery({
    queryKey: ["palette", "projects", workspace.id],
    queryFn: () => api.projects.list(workspace.id),
    enabled: open,
  });
  const allRequests = useQuery({
    queryKey: ["palette", "requests", workspace.id],
    queryFn: async () => {
      const cs = (await api.collections.list(workspace.id)).collections;
      const lists = await Promise.all(cs.map((c) => api.collections.requests(c.id).then((r) => ({ c, rs: r.requests }))));
      return lists.flatMap(({ c, rs }) => rs.map((r) => ({ ...r, collectionName: c.name })));
    },
    enabled: open,
  });

  useEffect(() => {
    if (!open) { setQ(""); setSel(0); return; }
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const cmds: Cmd[] = useMemo(() => {
    const list: Cmd[] = [];

    const eff = effectiveTheme(readMode());
    const nextTheme = eff === "dark" ? "light" : "dark";
    list.push(
      { id: "envs",         label: "Environments",                      group: "Manage",     run: () => { onClose(); onManageEnvs(); } },
      { id: "agents",       label: "Agents",                            group: "Manage",     run: () => { onClose(); onManageAgents(); } },
      { id: "settings",     label: "Workspace settings",                group: "Manage",     run: () => nav("/settings"), shortcut: "Mod+," },
      { id: "theme-toggle", label: `Switch to ${nextTheme} mode`, hint: `currently ${eff}`, group: "Appearance", run: () => setMode(nextTheme) },
      { id: "theme-system", label: "Match system theme",                group: "Appearance", run: () => setMode("system") },
      { id: "logout",       label: "Sign out",                          group: "Account",    run: async () => { await api.logout(); window.location.href = "/login"; } },
    );

    for (const p of (projects.data?.projects ?? []) as Project[]) {
      list.push({
        id: `p-${p.id}`,
        label: p.name,
        hint: p.is_default ? "project · default" : "project",
        group: "Projects",
        run: () => {
          writeActiveProjectId(workspace.id, p.id);
          nav(`/?prj=${p.id}`);
        },
      });
    }
    for (const r of (allRequests.data ?? []) as (RequestSummary & { collectionName: string })[]) {
      list.push({ id: `r-${r.id}`, label: r.name, hint: `${r.method} · ${r.collectionName}`, group: "Requests", run: () => nav(`/r/${r.id}`) });
    }
    return list;
  }, [allRequests.data, projects.data, workspace.id, nav, onClose, onManageEnvs, onManageAgents]);

  const ranked = useMemo(() => {
    if (!q.trim()) return cmds;
    return cmds
      .map((c) => ({ c, s: Math.max(score(c.label, q), score(c.hint ?? "", q) / 2) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.c);
  }, [cmds, q]);

  useEffect(() => { setSel(0); }, [q, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center pt-[12vh] p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card w-full max-w-xl shadow-[var(--shadow-strong)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent outline-none text-text placeholder:text-text-faint text-base"
            placeholder="Search collections, requests, settings…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, ranked.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); ranked[sel]?.run(); onClose(); }
              else if (e.key === "Escape") { e.preventDefault(); onClose(); }
            }}
          />
          <kbd className="kbd">{formatCombo("Esc")}</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto scroll-pane">
          {ranked.length === 0 && (
            <div className="px-4 py-6 text-sm muted">No matches.</div>
          )}
          {(() => {
            const groups: Array<{ name: string; items: Cmd[] }> = [];
            for (const cmd of ranked) {
              if (!groups.length || groups[groups.length - 1].name !== cmd.group) {
                groups.push({ name: cmd.group, items: [] });
              }
              groups[groups.length - 1].items.push(cmd);
            }
            let i = -1;
            return groups.map((g) => (
              <div key={g.name}>
                <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider muted-strong">{g.name}</div>
                {g.items.map((cmd) => {
                  i += 1;
                  const active = i === sel;
                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      className={
                        "w-full flex items-center gap-3 px-4 py-2 text-left text-sm " +
                        (active ? "bg-accent-soft text-text" : "hover:bg-surface-2")
                      }
                      onMouseEnter={() => setSel(i)}
                      onClick={() => { cmd.run(); onClose(); }}
                    >
                      <span className="flex-1 truncate">{cmd.label}</span>
                      {cmd.hint && <span className="text-xs muted-strong truncate">{cmd.hint}</span>}
                      {cmd.shortcut && <kbd className="kbd">{formatCombo(cmd.shortcut)}</kbd>}
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>
        <div className="border-t border-border px-4 py-2 text-[11px] muted-strong flex items-center gap-3">
          <span><kbd className="kbd">↑</kbd> <kbd className="kbd">↓</kbd> navigate</span>
          <span><kbd className="kbd">↵</kbd> open</span>
          <span className="ml-auto">rest</span>
        </div>
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-text-faint">
      <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.8" />
      <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
